import { FieldArray, useFormikContext } from 'formik'
import * as React from 'react'
import { ReactElement, useEffect, useMemo } from 'react'
import Fieldset from '../../Fieldset'
import { Stack } from '@fluentui/react'
import { DateTime } from 'luxon'
import LegacyLocalDatePicker from '../../../../components/LegacyLocalDatePicker'
import LineItemGroups from './LineItemGroups'
import FormikTextField from '../../../../components/Formik/TextField/FormikTextField'
import {
    AccountingId,
    InvoiceMessage,
    InvoiceMutationDto,
    InvoiceStatus,
    InvoiceType,
} from '../../../../config/finance/invoiceConfig'
import FinanceUnitField from './FinanceUnitField'
import SingleUnitDimensionFields from '../../Accounting/SingleUnitDimensionFields'
import { getFilePartyFromMeta, InvoiceProps } from './InvoiceForm'
import useUnits from '../../../../hooks/useUnits'
import { FormikHelpers } from 'formik/dist/types'
import { DimensionsDto } from '../../Accounting/DimensionFields'
import { Field, Link, makeStyles, MessageBar, MessageBarBody, shorthands, tokens } from '@fluentui/react-components'
import useModules from '../../../../hooks/module/useModules'
import BaseCombobox from '../../../Combobox/BaseCombobox'
import { ModuleType } from '../../../../context/GlobalContext'
import InvoiceChildren from './InvoiceChildren'
import { useHistory } from 'react-router-dom'
import BankTransactions from './BankTransactions'
import { useTranslation } from 'react-i18next'
import FormikField from '../../../Formik/FormikField'
import LanguageCombobox, { LanguageComboboxProps } from '../../../Combobox/i18n/LanguageCombobox'
import CurrencyCombobox, { CurrencyComboboxProps } from '../../../Combobox/i18n/CurrencyCombobox'
import FormContainer from '../../FormContainer'
import AttributesList from '../../Attributes/AttributesList'
import { AttributeContextType, AttributeViewType, EntityAttributeType } from '../../../../hooks/useAttributes'
import MoneyInput, { MoneyInputProps } from '../../../Input/MoneyInput'
import { CommitEventType } from '../../../Input/FormattableNumberInput'

const useStyles = makeStyles({
    root: { flexGrow: 1, flexBasis: 0, minWidth: 0 },
    alert: {
        ...shorthands.margin(tokens.spacingHorizontalS),
    },
})

const setMessage = (
    message: InvoiceMessage,
    setFieldValue: FormikHelpers<InvoiceMutationDto>['setFieldValue'],
): void => {
    setFieldValue('messageSubject', message?.subject)
    setFieldValue('messageBody', message?.body?.replaceAll('<br/>', '\n'))
}

const setPaymentMessage = (
    message: InvoiceMessage,
    setFieldValue: FormikHelpers<InvoiceMutationDto>['setFieldValue'],
): void => {
    setFieldValue('paymentMessageBody', message?.body?.replaceAll('<br/>', '\n'))
}

export default function InvoiceFieldSets(props: InvoiceProps): ReactElement {
    const history = useHistory()
    const { t: translate } = useTranslation()

    const styles = useStyles()
    const { hasParties, invoice, meta, useInitialInvoice } = props
    const { initialValues, values, setFieldValue } = useFormikContext<InvoiceMutationDto>()
    const isEndUserInvoice = useMemo<boolean>(() => ![InvoiceType.Split].includes(values.type), [values.type])
    const isSplitInvoice = useMemo<boolean>(() => [InvoiceType.Split].includes(values.type), [values.type])
    const isSubSplitInvoice = useMemo<boolean>(() => [InvoiceType.SubSplit].includes(values.type), [values.type])
    const context = useFormikContext<InvoiceMutationDto>()
    const { units } = useUnits()
    const modules = useModules()

    useEffect(() => {
        if (values.status === InvoiceStatus.New) {
            setFieldValue('number', meta.data.number)
        }

        setFieldValue('name', (useInitialInvoice && invoice.name) || meta.data.file.invoiceName || meta.data.file.name)
        setFieldValue('currency', (useInitialInvoice && invoice.price?.currency) || meta.data.file.currency || 'EUR')
        setFieldValue('language', (useInitialInvoice && invoice.language) || meta.data.file.language)
        setFieldValue('period', useInitialInvoice && invoice.period)

        setFieldValue(
            'unit',
            useInitialInvoice
                ? invoice.unit
                : (meta.data.file.unit && units.find(unit => meta.data.file.unit === unit.key)?.key) ||
                      (units.length === 1 && units[0].key),
        )
    }, [meta.data, units, isEndUserInvoice])

    useEffect(() => {
        setFieldValue(
            'accounting',
            useInitialInvoice && invoice.unit === values.unit
                ? initialValues.accounting
                : meta.data.file.accounting?.[values.unit],
        )
    }, [meta.data, values.unit, isEndUserInvoice])

    // Updates when language or currency changes.
    useEffect(() => {
        if (isEndUserInvoice && values.language) {
            if (useInitialInvoice) {
                setMessage(invoice.message, setFieldValue)
            } else {
                setMessage(
                    (meta.data.emailMessages || [])
                        .filter(message => message.language === values.language)
                        .sort((a, b) => a.currency?.localeCompare(b.currency || 'zzz') ?? 1)
                        .find(message => !message.currency || message.currency === values.currency),
                    setFieldValue,
                )
            }
        }
    }, [meta.data.emailMessages, values.language, values.currency])

    // Updates when language changes.
    useEffect(() => {
        if (!!meta?.data?.file?.paymentCurrency && isEndUserInvoice && values.language) {
            if (useInitialInvoice) {
                setPaymentMessage(invoice.paymentMessage, setFieldValue)
            } else {
                setPaymentMessage(
                    (meta.data.paymentMessages || [])
                        .filter(message => message.language === values.language)
                        .sort((a, b) => a.currency?.localeCompare(b.currency || 'zzz') ?? 1)
                        .find(message => !message.currency || message.currency === meta?.data?.file?.paymentCurrency),
                    setFieldValue,
                )
            }
        }
    }, [meta.data.paymentMessages, values.language])

    useEffect(() => {
        const template = values.templateId && props.templates?.find(template => template.id === values.templateId)
        let term = template?.term ?? 14

        if (useInitialInvoice && invoice.recipient?.debtorId === values.debtorPivotId) {
            term = invoice.term ?? term
        } else if (!template || template.type === InvoiceType.Regular) {
            term = getFilePartyFromMeta(values.debtorPivotId, meta.data.fileParties)?.term || term
        }
        setFieldValue('term', term)
    }, [values.debtorPivotId, meta.data.fileParties])

    if (!hasParties) {
        return <></>
    }

    return (
        <Stack tokens={{ childrenGap: 'm' }}>
            <Fieldset legend={translate('invoice.edit.form.payment.label')} canToggle>
                <Stack horizontal tokens={{ childrenGap: 'm' }}>
                    <FormikTextField
                        context={context}
                        label={translate('invoice.edit.form.payment.number')}
                        fieldName="number"
                        textFieldProps={{ required: true, styles }}
                    />
                    <LegacyLocalDatePicker
                        value={values.date}
                        onSelectDate={date => setFieldValue('date', date)}
                        datePickerProps={{
                            label: translate('invoice.edit.form.payment.date'),
                            minDate: DateTime.local().minus({ years: 1 }).toBSON(),
                            maxDate: DateTime.local().plus({ years: 1 }).toBSON(),
                            styles,
                        }}
                    />
                    <FormikTextField
                        context={context}
                        label={translate('invoice.edit.form.payment.term')}
                        fieldName="term"
                        textFieldProps={{
                            required: true,
                            styles,
                            prefix: translate('invoice.edit.form.header.term_prefix'),
                            suffix: translate('invoice.edit.form.header.term_suffix'),
                            inputClassName: 'ms-textAlignRight',
                        }}
                    />
                </Stack>
                <Stack horizontal tokens={{ childrenGap: 'm' }}>
                    <FormikField<CurrencyComboboxProps>
                        required
                        context={context}
                        label={translate('invoice.edit.form.payment.currency')}
                        fieldName={'currency'}
                        component={CurrencyCombobox}
                        componentProps={{
                            selectedOptions: [values.currency],
                            required: true,
                            onOptionSelect: (_, data) => {
                                setFieldValue('currency', data.selectedOptions?.[0])
                            },
                        }}
                    />
                    {(!!meta?.data?.file?.paymentCurrency || !!values.paymentPrice) && (
                        <FormikField<MoneyInputProps>
                            context={context}
                            label={translate('invoice.edit.form.payment.paymentprice')}
                            fieldName={'paymentPrice'}
                            component={MoneyInput}
                            componentProps={{
                                currency: meta.data.file.paymentCurrency,
                                value: values.paymentPrice,
                                onCommit: (eventType: CommitEventType, value: string) => {
                                    if (eventType !== CommitEventType.OnLoad) {
                                        setFieldValue('paymentPrice', value)
                                    }
                                },
                            }}
                        />
                    )}
                </Stack>
            </Fieldset>
            <Fieldset legend={translate('invoice.edit.form.invoiceline.label')} canToggle open>
                {!isSubSplitInvoice && (
                    <FieldArray name="lineItemGroups">
                        {arrayHelpers => <LineItemGroups {...arrayHelpers} {...props} />}
                    </FieldArray>
                )}
                {isSubSplitInvoice && (
                    <MessageBar intent={'info'}>
                        <MessageBarBody>
                            {translate('invoice.edit.info.split')}{' '}
                            <Link onClick={() => history.push(`/finance/invoices/${invoice.parent?.id}/edit`)}>
                                {invoice.parent?.number}
                            </Link>
                            .
                        </MessageBarBody>
                    </MessageBar>
                )}
            </Fieldset>
            {isSplitInvoice && (
                <Fieldset legend={translate('invoice.edit.form.debtors.label')} canToggle open>
                    <FieldArray name="children">
                        {arrayHelpers => <InvoiceChildren {...arrayHelpers} {...props} />}
                    </FieldArray>
                </Fieldset>
            )}
            {isEndUserInvoice && (
                <Fieldset legend={translate('invoice.edit.form.correspondence.label')} canToggle>
                    <Stack horizontal tokens={{ childrenGap: 'm' }}>
                        <FormikTextField
                            context={context}
                            label={translate('invoice.edit.form.header.recipientemail')}
                            fieldName="recipientEmail"
                            textFieldProps={{ styles }}
                        />
                        <FormikTextField
                            context={context}
                            label={translate('invoice.edit.form.header.recipientreference')}
                            fieldName="recipientReference"
                            textFieldProps={{ styles }}
                        />
                    </Stack>
                    <Stack horizontal tokens={{ childrenGap: 'm' }}>
                        <FormikTextField
                            context={context}
                            label={translate('invoice.edit.form.correspondence.name')}
                            fieldName="name"
                            textFieldProps={{ styles }}
                        />
                        <FormikField<LanguageComboboxProps>
                            required
                            context={context}
                            label={translate('invoice.edit.form.correspondence.language')}
                            fieldName={'language'}
                            component={LanguageCombobox}
                            componentProps={{
                                selectedOptions: [values.language],
                                required: true,
                                onOptionSelect: (_, data) => {
                                    setFieldValue('language', data.selectedOptions?.[0])
                                },
                            }}
                        />
                    </Stack>

                    <FormikTextField
                        context={context}
                        label={translate('invoice.edit.form.correspondence.messagesubject')}
                        fieldName="messageSubject"
                    />
                    <FormikTextField
                        context={context}
                        label={translate('invoice.edit.form.correspondence.messagebody')}
                        fieldName="messageBody"
                        textFieldProps={{ multiline: true, rows: 5, autoAdjustHeight: true }}
                    />
                    {meta?.data?.file?.paymentCurrency && (
                        <FormikTextField
                            context={context}
                            label={translate('invoice.edit.form.correspondence.paymentmessagebody')}
                            fieldName="paymentMessageBody"
                            textFieldProps={{ multiline: true, rows: 5, autoAdjustHeight: true }}
                        />
                    )}
                </Fieldset>
            )}

            {modules.has(ModuleType.Accounting) && values.unit && (
                <Fieldset legend={translate('invoice.edit.form.accounting.label')} canToggle>
                    <SingleUnitDimensionFields
                        values={values.accounting}
                        setValue={(property: keyof DimensionsDto, item: AccountingId) =>
                            setFieldValue(`accounting.${property}`, item)
                        }
                        unitKey={values.unit}
                    />
                </Fieldset>
            )}

            <Fieldset legend={translate('invoice.edit.form.other.label')} canToggle>
                {isEndUserInvoice && (
                    <FormikTextField
                        context={context}
                        label={translate('invoice.edit.form.other.description')}
                        fieldName="description"
                        textFieldProps={{ multiline: true, rows: 3, autoAdjustHeight: true }}
                    />
                )}
                <FormikTextField
                    context={context}
                    label={translate('invoice.edit.form.other.note')}
                    fieldName="note"
                    textFieldProps={{ multiline: true, rows: 3, autoAdjustHeight: true }}
                />
                <Stack horizontal tokens={{ childrenGap: 's1' }}>
                    <FinanceUnitField />
                </Stack>
                <Field label={translate('invoice.edit.form.other.type')} required>
                    <BaseCombobox<InvoiceType>
                        required
                        entities={Object.values(InvoiceType)}
                        getEntityLabel={invoiceType =>
                            translate(`invoice.type.${invoiceType.toLowerCase().replaceAll(/[-_]/g, '')}`)
                        }
                        getEntityValue={invoiceType => invoiceType.toString()}
                        selectedOptions={[values.type].filter(Boolean)}
                        onOptionSelect={(_, data) => setFieldValue('type', data.optionValue)}
                    />
                </Field>
            </Fieldset>
            {modules.has(ModuleType.Attributes) && (
                <Fieldset legend={translate('invoice.edit.form.attributes.label')} canToggle>
                    <FormContainer>
                        <AttributesList
                            {...props}
                            entityType={EntityAttributeType.Invoice}
                            context={[AttributeContextType.Invoice]}
                            viewType={AttributeViewType.Input}
                            orientation={'horizontal'}
                            entity={values}
                            entityId={invoice?.id}
                            setFieldValue={setFieldValue}
                        />
                    </FormContainer>
                </Fieldset>
            )}
            <Fieldset legend={translate('invoice.edit.form.banktransactions.label')} canToggle>
                <BankTransactions {...props} bankTransactions={props.invoice?.bankTransactions} />
            </Fieldset>
        </Stack>
    )
}
